import { useState, useCallback } from 'react'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listStock,
  adjustInventory,
  type StockItem,
  type StockListParams,
} from '../../api/stock'
import { ApiError } from '../../api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { PageHeader } from '@/components/PageHeader'
import { Input } from '@/components/ui/input'

// ---- Inline-Bestands-Editor ----
function InventoryCell({
  item,
  onSave,
}: {
  item: StockItem
  onSave: (sku: string, delta: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [delta, setDelta] = useState('')

  function handleSave() {
    const n = parseInt(delta, 10)
    if (!isNaN(n) && n !== 0) {
      onSave(item.sku, n)
    }
    setEditing(false)
    setDelta('')
  }

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`font-mono tabular-nums hover:underline ${
          item.quantity_available <= (item.quantity_alert ?? 0)
            ? 'text-red-600 font-semibold'
            : 'text-gray-900'
        }`}
        title="Klicken zum Anpassen"
      >
        {item.quantity_available}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={delta}
        onChange={(e) => setDelta(e.target.value)}
        placeholder="±0"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') {
            setEditing(false)
            setDelta('')
          }
        }}
        className="w-16 border border-blue-400 rounded px-1 py-0.5 text-xs font-mono text-center
                   focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <button
        onClick={handleSave}
        className="text-xs text-green-600 hover:text-green-800 font-medium px-1"
      >
        ✓
      </button>
      <button
        onClick={() => {
          setEditing(false)
          setDelta('')
        }}
        className="text-xs text-gray-400 hover:text-gray-600 px-1"
      >
        ✕
      </button>
    </div>
  )
}

// ---- Hauptkomponente ----
export default function StockList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [minId, setMinId] = useState<number | undefined>()
  const [history, setHistory] = useState<number[]>([]) // Cursor-History für Zurück
  const [feedback, setFeedback] = useState<{ sku: string; msg: string } | null>(null)

  // Debounce Suche (300ms)
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setMinId(undefined)
    setHistory([])
    const timer = setTimeout(() => setDebouncedSearch(value), 300)
    return () => clearTimeout(timer)
  }, [])

  const params: StockListParams = {
    search: debouncedSearch || undefined,
    min_id: minId,
    limit: 50,
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['stock', params],
    queryFn: () => listStock(params),
    staleTime: 30_000,
  })

  // Inventory-Anpassung
  const adjustMutation = useMutation({
    mutationFn: ({ sku, delta }: { sku: string; delta: number }) =>
      adjustInventory(sku, delta),
    onSuccess: (updated) => {
      setFeedback({ sku: updated.sku, msg: `Bestand → ${updated.quantity_available}` })
      void queryClient.invalidateQueries({ queryKey: ['stock'] })
      setTimeout(() => setFeedback(null), 3000)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Fehler beim Speichern'
      setFeedback({ sku: '', msg })
      setTimeout(() => setFeedback(null), 4000)
    },
  })

  function handleNextPage() {
    if (data?.next_min_id) {
      setHistory((h) => [...h, minId ?? 0])
      setMinId(data.next_min_id)
    }
  }

  function handlePrevPage() {
    const prev = history[history.length - 1]
    setHistory((h) => h.slice(0, -1))
    setMinId(prev === 0 ? undefined : prev)
  }

  const columns: Column<StockItem>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (item) => (
        <>
          <div className="font-medium truncate max-w-xs">{item.name}</div>
          {item.manufacturer && (
            <div className="text-xs text-muted-foreground">{item.manufacturer}</div>
          )}
        </>
      ),
    },
    {
      key: 'sku',
      header: 'SKU',
      className: 'font-mono text-xs',
    },
    {
      key: 'merchant_sku',
      header: 'Händl.ArtNr.',
      className: 'font-mono text-xs',
      cell: (item) => <GrundartikelLink code={item.merchant_sku} />,
    },
    {
      key: 'barcode',
      header: 'EAN',
      className: 'font-mono text-xs',
      cell: (item) => item.barcode ?? '—',
    },
    {
      key: 'quantity_available',
      header: 'Bestand',
      className: 'text-right',
      cell: (item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <InventoryCell
            item={item}
            onSave={(sku, delta) => adjustMutation.mutate({ sku, delta })}
          />
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Preis',
      className: 'text-right font-mono',
      cell: (item) => `${Number(item.price).toFixed(2)} €`,
    },
    {
      key: 'location_fields',
      header: 'Lagerfächer',
      cell: (item) =>
        item.location_fields && item.location_fields.length > 0
          ? item.location_fields.join(', ')
          : '—',
    },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Lager" />

      {/* Suchfeld */}
      <div className="mb-4">
        <Input
          type="search"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Suche nach Name, SKU, EAN, MPN…"
          className="max-w-md w-full"
        />
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          className={`mb-4 text-sm ${feedback.sku ? 'text-green-600' : 'text-destructive'}`}
          role="status"
        >
          {feedback.sku && <span className="font-mono font-medium">{feedback.sku} — </span>}
          {feedback.msg}
        </div>
      )}

      {/* Ladezustand */}
      {isLoading && (
        <div className="text-center py-12 text-muted-foreground text-sm">Lade Artikel…</div>
      )}

      {/* Fehler */}
      {isError && (
        <p className="text-sm text-destructive">
          Fehler: {error instanceof ApiError ? error.message : 'Unbekannter Fehler'}
        </p>
      )}

      {/* Tabelle */}
      {data && (
        <DataTable
          data={data.items}
          columns={columns}
          rowKey={(item) => item.id}
          onRowClick={(item) => navigate(`/stock/${encodeURIComponent(item.sku)}`)}
          emptyMessage="Keine Artikel gefunden."
          pagination={{
            hasMore: data.has_more,
            onNext: handleNextPage,
            onPrev: handlePrevPage,
            canPrev: history.length > 0,
            count: data.items.length,
          }}
        />
      )}
    </div>
  )
}
